'use client';

import { useEffect } from 'react';



import { usePricingLookup } from '@/app/lib/hooks/use-pricing-lookup';
import {
  useRatesPricingContext,
  resetFilters,
  setOptions,
  setItems,
} from '@/app/providers/rates-pricing';
import initialState from '@/app/providers/rates-pricing/initial-state';
import { InfoTag } from '@/components/alert';
import { Button } from '@/components/button';
import { RadioButtonGroup, RadioButtonVariant } from '@/components/input';
import { Loader } from '@/components/loader';
import { PlanList } from '@/components/plan-list';
import { Text } from '@/components/text';

import { type FilterItem, Filters } from './filters';
import { PlanIdInput } from './plan-id';
import { PostcodeInput } from './postcode';
import {
  ProvidedFilters,
  type FiltersType,
  RatesSearchParams,
} from './rates-prices.types';

import type { BPID } from '@energy-retailer/schema';

export const RatesPricesForm = () => {
  const { state, dispatch } = useRatesPricingContext();

  const {
    data,
    isLoading,
    postcode,
    planID,
    searchParam,
    refetch,
    setPostcode,
    setPlanID,
    setSearchParam,
  } = usePricingLookup();

  const radioButtonOptions = [
    {
      id: RatesSearchParams.Postcode,
      name: 'Postcode',
    },
    {
      id: RatesSearchParams.PlanID,
      name: 'Plan ID',
      description:
        'The Basic Plan Information Document (BPID) or Energy Fact Sheet (EFS) number.',
    },
  ];

  const filters = {
    customerType: state.filters.customerType.id,
    energyType: state.filters.energyType.id,
    tariffType:
      state.filters.tariffType.id !== '' ? state.filters.tariffType.name : '',
    planName:
      state.filters.planName.id !== '' ? state.filters.planName.name : '',
  };

  const getValue = (value) =>
    (typeof value === 'string' ? value.toUpperCase() : value) as string;

  function filterArray(array: BPID[], appliedFilters: FiltersType): BPID[] {
    const filterKeys = Object.keys(appliedFilters);
    return array.filter((item) => {
      // validates all filter criteria
      return filterKeys.every((key) => {
        // ignores an empty filter
        if (appliedFilters[key] === '') return true;
        return getValue(item[key]) === getValue(appliedFilters[key]);
      });
    });
  }

  useEffect(() => {
    if (data?.items && data.items.length > 0) {
      const result = (filterType: ProvidedFilters) =>
        data.items.reduce((acc: FilterItem[], d) => {
          const found = acc.find((a: FilterItem) => a.name === d[filterType]);

          const value: FilterItem = {
            name: String(d[filterType]),
            id: String(d[filterType]).toLowerCase().replaceAll(' ', '-'),
            disabled: false,
          };

          if (!found && value.name !== '') {
            acc.push(value);
          }

          return acc;
        }, []);

      dispatch(
        setOptions({
          tariffType: [
            initialState.filters.tariffType,
            ...result(ProvidedFilters.TariffType),
          ],
          planName: [
            initialState.filters.planName,
            ...result(ProvidedFilters.PlanName),
          ],
        })
      );

      if (searchParam === RatesSearchParams.PlanID) {
        dispatch(setItems(data.items));
      } else {
        dispatch(setItems(filterArray(data.items, filters)));
      }
    } else {
      dispatch(setItems([]));
      dispatch(resetFilters());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dispatch doesnt need to be listerned for
  }, [data]);

  useEffect(() => {
    if (data?.items && data.items.length > 0) {
      dispatch(setItems(filterArray(data.items, filters)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dispatch doesnt need to be listerned for
  }, [state.filters]);

  useEffect(() => {
    dispatch(resetFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- set state doesnt need to be listerned for
  }, [searchParam]);

  return (
    <>
      <div className="bg-gray-50 pb-i pt-d">
        <div className="container">
          <div className="grid grid-cols-1 max-md:gap-y-c md:grid-cols-2 lg:grid-cols-6">
            <div className="col-span-1 lg:col-span-2 lg:col-start-2">
              <RadioButtonGroup
                disabled={false}
                heading="Search by:"
                showHeading
                headingVariant="body3"
                onChange={(val) => {
                  setSearchParam(val as RatesSearchParams);
                }}
                options={radioButtonOptions}
                selected={searchParam}
                variant={RadioButtonVariant.RIGHT}
              />
            </div>
            <div className="col-span-1 lg:col-span-2">
              <form
                onSubmit={(evt) => {
                  evt.preventDefault();
                  refetch().catch(console.error);
                }}
                className="flex h-full flex-col items-center gap-y-b rounded-rad-b bg-white p-c max-md:py-e"
              >
                {searchParam === RatesSearchParams.Postcode ? (
                  <PostcodeInput
                    value={postcode}
                    onChange={(val) => {
                      setPostcode(val);
                    }}
                  />
                ) : (
                  <PlanIdInput
                    value={planID}
                    onChange={(val) => {
                      setPlanID(val);
                    }}
                  />
                )}
                <Button
                  type="submit"
                  variant="primary"
                  disabled={
                    (searchParam === RatesSearchParams.PlanID &&
                      planID.length < 10) ||
                    (searchParam === RatesSearchParams.Postcode &&
                      postcode.length < 4)
                  }
                >
                  View energy info
                </Button>
              </form>
            </div>
          </div>
          <div className="grid grid-cols-1 pt-d">
            <div className="flex w-full justify-center">
              <Button
                type="button"
                variant="tertiary"
                onClick={() => {
                  setPlanID('');
                  setPostcode('');

                  refetch().catch(console.error);
                }}
                disabled={Boolean(
                  (postcode.length === 4 || planID.length === 10) &&
                    data?.items &&
                    data.items.length > 0
                )}
              >
                See All
              </Button>
            </div>
          </div>
          {(postcode.length === 4 || planID.length === 11) &&
          data?.items &&
          data.items.length === 0 ? (
            <div className="grid grid-cols-1 pt-d max-md:gap-y-c lg:grid-cols-6">
              <div className="col-span-1 lg:col-span-4 lg:col-start-2">
                <InfoTag>
                  <Text
                    variant="body2"
                    as="p"
                    className="text-center"
                  >
                    Whoops! It looks like there is no plan that matches that{' '}
                    {searchParam === RatesSearchParams.Postcode
                      ? 'postcode'
                      : 'ID'}
                    , double check you have the details correct or contact us
                    and we&apos;ll help you out.
                  </Text>
                </InfoTag>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {isLoading ? (
        <div className="align-center flex flex-col gap-y-f py-f">
          <Loader />
        </div>
      ) : null}
      {data?.items && data.items.length > 0 ? (
        <div className="flex flex-col gap-y-f py-f">
          {searchParam === RatesSearchParams.Postcode ? <Filters /> : null}
          <PlanList
            showAll={planID === '' && postcode === ''}
            isPostcodeSearch={searchParam === RatesSearchParams.Postcode}
          />

          {state.items.length === 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-6">
              <div className="col-span-1 lg:col-span-4 lg:col-start-2">
                <InfoTag>
                  <Text
                    variant="body2"
                    as="p"
                    className="text-center"
                  >
                    Whoops! There are no plans that match your filtered search,
                    try using different filters, or contact us and we&apos;ll
                    help you out.
                  </Text>
                </InfoTag>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
};
